import { useState, useMemo } from 'react';
import { Plus, X, Building2, Target, AlertTriangle, TrendingUp, Lightbulb, HelpCircle, Wrench, Sparkles, Search } from 'lucide-react';
import type { AboutCompany } from '../../types/settings';
import type { IcpConfig } from '../../types/icp';
import { ICP_LIMITS, COMPANY_SIZE_OPTIONS } from '../../types/icp';
import { useIcpMasterData } from '../../hooks/useIcpData';
import { BoldPrefixInput } from './BoldPrefixInput';

interface AboutCompanySectionProps {
  data: AboutCompany;
  onChange: (data: AboutCompany) => void;
  onReResearch?: () => void;
  icpConfig: IcpConfig | null;
  onIcpChange: (config: IcpConfig) => void;
}

const SENIORITY_ORDER = ['Founder/Owner', 'C-Suite', 'VP', 'Director', 'Head', 'Manager'];

const COUNTRY_SHORT_NAMES: Record<string, string> = {
  'United Arab Emirates': 'UAE',
  'United Kingdom': 'UK',
  'United States': 'US',
  'Saudi Arabia': 'KSA',
  'South Korea': 'S. Korea',
  'New Zealand': 'NZ',
  'South Africa': 'SA',
  'Czech Republic': 'Czechia',
  'Hong Kong': 'HK',
};

interface LocationEntry {
  key: string;
  label: string;
  tagLabel: string;
  searchTerms: string[];
  countryId: number;
  regionId: number | null;
}

function LimitBadge({ current, min, max }: { current: number; min: number; max: number }) {
  const isBelow = current < min;
  const isAtMax = current >= max;
  return (
    <span className={`text-xs font-bold px-2.5 py-1 rounded-lg ${
      isBelow ? 'bg-red-100 text-red-600' : isAtMax ? 'bg-green-100 text-green-700' : 'bg-[#1875FF]/10 text-[#1875FF]'
    }`}>
      {current}/{max}
    </span>
  );
}

export function AboutCompanySection({ data, onChange, onReResearch, icpConfig, onIcpChange }: AboutCompanySectionProps) {
  const { masterData, loading: masterLoading } = useIcpMasterData();

  const [titleSearch, setTitleSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [industrySearch, setIndustrySearch] = useState('');
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showIndustryDropdown, setShowIndustryDropdown] = useState(false);
  const [showTitleDropdown, setShowTitleDropdown] = useState(false);

  const config: IcpConfig = icpConfig || { countries: [], industry_ids: [], title_ids: [], company_sizes: [] };

  const selectedCountryIds = useMemo(() => new Set(config.countries.map(c => c.country_id)), [config.countries]);
  const selectedIndustryIds = useMemo(() => new Set(config.industry_ids), [config.industry_ids]);
  const selectedTitleIds = useMemo(() => new Set(config.title_ids), [config.title_ids]);

  const locationIndex = useMemo<LocationEntry[]>(() => {
    if (!masterData) return [];
    const entries: LocationEntry[] = [];
    for (const country of masterData.countries) {
      const shortName = COUNTRY_SHORT_NAMES[country.country_name] || country.country_name;
      const countryTerms = [country.country_name.toLowerCase(), shortName.toLowerCase(), country.iso_code.toLowerCase()];
      entries.push({
        key: `country-${country.id}`,
        label: country.country_name,
        tagLabel: shortName,
        searchTerms: countryTerms,
        countryId: country.id,
        regionId: null,
      });
      for (const region of country.regions) {
        entries.push({
          key: `region-${country.id}-${region.id}`,
          label: `${region.region_name}, ${country.country_name}`,
          tagLabel: `${region.region_name}, ${shortName}`,
          searchTerms: [region.region_name.toLowerCase(), ...countryTerms],
          countryId: country.id,
          regionId: region.id,
        });
      }
    }
    return entries;
  }, [masterData]);

  const selectedLocationKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const sel of config.countries) {
      if (sel.region_ids.length === 0) {
        keys.add(`country-${sel.country_id}`);
      } else {
        for (const rid of sel.region_ids) {
          keys.add(`region-${sel.country_id}-${rid}`);
        }
      }
    }
    return keys;
  }, [config.countries]);

  const filteredLocations = useMemo(() => {
    if (!locationSearch.trim()) return [];
    const q = locationSearch.toLowerCase().trim();
    return locationIndex.filter(entry => {
      if (selectedLocationKeys.has(entry.key)) return false;
      if (entry.regionId === null && selectedCountryIds.has(entry.countryId)) {
        const sel = config.countries.find(c => c.country_id === entry.countryId);
        if (sel && sel.region_ids.length === 0) return false;
      }
      if (entry.regionId !== null) {
        const sel = config.countries.find(c => c.country_id === entry.countryId);
        if (sel && sel.region_ids.length === 0) return false;
      }
      return entry.searchTerms.some(term => term.includes(q));
    }).slice(0, 10);
  }, [locationSearch, locationIndex, selectedLocationKeys, selectedCountryIds, config.countries]);

  const selectedLocationTags = useMemo(() => {
    const tags: { key: string; tagLabel: string; countryId: number; regionId: number | null }[] = [];
    for (const sel of config.countries) {
      const country = masterData?.countries.find(c => c.id === sel.country_id);
      if (!country) continue;
      const shortName = COUNTRY_SHORT_NAMES[country.country_name] || country.country_name;
      if (sel.region_ids.length === 0) {
        tags.push({ key: `country-${sel.country_id}`, tagLabel: shortName, countryId: sel.country_id, regionId: null });
      } else {
        for (const rid of sel.region_ids) {
          const region = country.regions.find(r => r.id === rid);
          if (region) {
            tags.push({ key: `region-${sel.country_id}-${rid}`, tagLabel: `${region.region_name}, ${shortName}`, countryId: sel.country_id, regionId: rid });
          }
        }
      }
    }
    return tags;
  }, [config.countries, masterData]);

  const selectLocation = (entry: LocationEntry) => {
    if (entry.regionId === null) {
      if (selectedCountryIds.has(entry.countryId)) {
        const updated = config.countries.map(c =>
          c.country_id === entry.countryId ? { ...c, region_ids: [] } : c
        );
        onIcpChange({ ...config, countries: updated });
      } else if (config.countries.length < ICP_LIMITS.countries.max) {
        onIcpChange({ ...config, countries: [...config.countries, { country_id: entry.countryId, region_ids: [] }] });
      }
    } else {
      const existing = config.countries.find(c => c.country_id === entry.countryId);
      if (existing) {
        if (!existing.region_ids.includes(entry.regionId)) {
          const updated = config.countries.map(c =>
            c.country_id === entry.countryId ? { ...c, region_ids: [...c.region_ids, entry.regionId!] } : c
          );
          onIcpChange({ ...config, countries: updated });
        }
      } else if (config.countries.length < ICP_LIMITS.countries.max) {
        onIcpChange({ ...config, countries: [...config.countries, { country_id: entry.countryId, region_ids: [entry.regionId] }] });
      }
    }
    setLocationSearch('');
    setShowLocationDropdown(false);
  };

  const removeLocationTag = (tag: { countryId: number; regionId: number | null }) => {
    if (tag.regionId === null) {
      onIcpChange({ ...config, countries: config.countries.filter(c => c.country_id !== tag.countryId) });
    } else {
      const existing = config.countries.find(c => c.country_id === tag.countryId);
      if (!existing) return;
      const newRegionIds = existing.region_ids.filter(r => r !== tag.regionId);
      if (newRegionIds.length === 0) {
        onIcpChange({ ...config, countries: config.countries.filter(c => c.country_id !== tag.countryId) });
      } else {
        const updated = config.countries.map(c =>
          c.country_id === tag.countryId ? { ...c, region_ids: newRegionIds } : c
        );
        onIcpChange({ ...config, countries: updated });
      }
    }
  };

  const filteredIndustries = useMemo(() => {
    if (!masterData) return [];
    if (!industrySearch) return masterData.industries;
    const q = industrySearch.toLowerCase();
    return masterData.industries.filter(i => i.industry_name.toLowerCase().includes(q));
  }, [masterData, industrySearch]);

  const filteredTitles = useMemo(() => {
    if (!masterData) return [];
    if (!titleSearch) return masterData.titles;
    const q = titleSearch.toLowerCase();
    return masterData.titles.filter(t => t.title_name.toLowerCase().includes(q));
  }, [masterData, titleSearch]);

  const titlesBySeniority = useMemo(() => {
    const unselected = filteredTitles.filter(t => !selectedTitleIds.has(t.id));
    const groups: Record<string, typeof filteredTitles> = {};
    for (const level of SENIORITY_ORDER) groups[level] = [];
    for (const t of unselected) {
      if (!groups[t.seniority_level]) groups[t.seniority_level] = [];
      groups[t.seniority_level].push(t);
    }
    return Object.entries(groups).filter(([, titles]) => titles.length > 0);
  }, [filteredTitles, selectedTitleIds]);

  const toggleIndustry = (industryId: number) => {
    if (selectedIndustryIds.has(industryId)) {
      onIcpChange({ ...config, industry_ids: config.industry_ids.filter(id => id !== industryId) });
    } else if (config.industry_ids.length < ICP_LIMITS.industries.max) {
      onIcpChange({ ...config, industry_ids: [...config.industry_ids, industryId] });
    }
  };

  const toggleTitle = (titleId: number) => {
    if (selectedTitleIds.has(titleId)) {
      onIcpChange({ ...config, title_ids: config.title_ids.filter(id => id !== titleId) });
    } else if (config.title_ids.length < ICP_LIMITS.titles.max) {
      onIcpChange({ ...config, title_ids: [...config.title_ids, titleId] });
    }
  };

  const toggleCompanySize = (size: string) => {
    const current = config.company_sizes;
    const updated = current.includes(size) ? current.filter(s => s !== size) : [...current, size];
    onIcpChange({ ...config, company_sizes: updated });
  };

  const getIndustryName = (industryId: number) =>
    masterData?.industries.find(i => i.id === industryId)?.industry_name || '';

  const getTitleName = (titleId: number) =>
    masterData?.titles.find(t => t.id === titleId)?.title_name || '';

  const addPainPoint = () => {
    onChange({ ...data, pain_points: [...data.pain_points, { id: crypto.randomUUID(), text: '' }] });
  };
  const updatePainPoint = (id: string, text: string) => {
    onChange({ ...data, pain_points: data.pain_points.map(p => p.id === id ? { ...p, text } : p) });
  };
  const removePainPoint = (id: string) => {
    onChange({ ...data, pain_points: data.pain_points.filter(p => p.id !== id) });
  };

  const addBenefit = () => {
    onChange({ ...data, benefits: [...data.benefits, { id: crypto.randomUUID(), text: '' }] });
  };
  const updateBenefit = (id: string, text: string) => {
    onChange({ ...data, benefits: data.benefits.map(b => b.id === id ? { ...b, text } : b) });
  };
  const removeBenefit = (id: string) => {
    onChange({ ...data, benefits: data.benefits.filter(b => b.id !== id) });
  };

  const addCompetitor = () => {
    onChange({ ...data, competitors: [...(data.competitors || []), { id: crypto.randomUUID(), text: '' }] });
  };
  const updateCompetitor = (id: string, text: string) => {
    onChange({ ...data, competitors: (data.competitors || []).map(c => c.id === id ? { ...c, text } : c) });
  };
  const removeCompetitor = (id: string) => {
    onChange({ ...data, competitors: (data.competitors || []).filter(c => c.id !== id) });
  };

  const addSolution = () => {
    onChange({ ...data, solutions: [...(data.solutions || []), { id: crypto.randomUUID(), text: '' }] });
  };
  const updateSolution = (id: string, text: string) => {
    onChange({ ...data, solutions: (data.solutions || []).map(s => s.id === id ? { ...s, text } : s) });
  };
  const removeSolution = (id: string) => {
    onChange({ ...data, solutions: (data.solutions || []).filter(s => s.id !== id) });
  };

  const addDesire = () => {
    onChange({ ...data, desires: [...(data.desires || []), { id: crypto.randomUUID(), text: '' }] });
  };
  const updateDesire = (id: string, text: string) => {
    onChange({ ...data, desires: (data.desires || []).map(d => d.id === id ? { ...d, text } : d) });
  };
  const removeDesire = (id: string) => {
    onChange({ ...data, desires: (data.desires || []).filter(d => d.id !== id) });
  };

  const addQuestion = () => {
    onChange({ ...data, prospect_questions: [...(data.prospect_questions || []), { id: crypto.randomUUID(), text: '' }] });
  };
  const updateQuestion = (id: string, text: string) => {
    onChange({ ...data, prospect_questions: (data.prospect_questions || []).map(q => q.id === id ? { ...q, text } : q) });
  };
  const removeQuestion = (id: string) => {
    onChange({ ...data, prospect_questions: (data.prospect_questions || []).filter(q => q.id !== id) });
  };

  if (masterLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-[#1875FF] border-t-transparent rounded-full animate-spin" />
        <span className="ml-3 text-sm text-gray-500">Loading ICP data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Company Overview */}
      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-[box-shadow,border-color,transform] duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
              <Building2 className="w-5 h-5 text-[#1875FF]" />
            </div>
            <h3 className="text-lg font-extrabold text-gray-900 font-display">Company Overview</h3>
          </div>
          {onReResearch && (
            <button
              onClick={onReResearch}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-lg hover:shadow-md transition-all"
              title="AI Generate from website"
            >
              <Sparkles className="w-3.5 h-3.5" />
              AI Generate
            </button>
          )}
        </div>
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Products / Services</label>
            <textarea
              value={data.products_services}
              onChange={(e) => onChange({ ...data, products_services: e.target.value })}
              placeholder="Describe your products or services..."
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all resize-none text-sm"
              rows={4}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">USP Statement</label>
            <textarea
              value={data.usp_statement}
              onChange={(e) => onChange({ ...data, usp_statement: e.target.value })}
              placeholder="What makes you unique?..."
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all resize-none text-sm"
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* Target Audience / ICP */}
      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-[box-shadow,border-color,transform] duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
            <Target className="w-5 h-5 text-[#1875FF]" />
          </div>
          <h3 className="text-lg font-extrabold text-gray-900 font-display">Target Audience / ICP</h3>
        </div>

        <div className="space-y-6">
          {/* Locations */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Countries & Regions</label>
              <LimitBadge current={config.countries.length} min={ICP_LIMITS.countries.min} max={ICP_LIMITS.countries.max} />
            </div>

            {selectedLocationTags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {selectedLocationTags.map(tag => (
                  <span key={tag.key} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#1875FF]/10 text-[#1875FF] rounded-full text-sm font-medium">
                    {tag.tagLabel}
                    <button onClick={() => removeLocationTag(tag)} className="hover:bg-[#1875FF]/20 rounded-full p-0.5 transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {config.countries.length < ICP_LIMITS.countries.max || selectedLocationTags.length > 0 ? (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={locationSearch}
                    onChange={(e) => {
                      setLocationSearch(e.target.value);
                      setShowLocationDropdown(true);
                    }}
                    onFocus={() => { if (locationSearch.trim()) setShowLocationDropdown(true); }}
                    onBlur={() => setTimeout(() => setShowLocationDropdown(false), 200)}
                    placeholder="Type a city or country..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all text-sm"
                  />
                </div>
                {showLocationDropdown && filteredLocations.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white border-2 border-gray-200 rounded-xl shadow-2xl shadow-[#1875FF]/20 max-h-48 overflow-auto">
                    {filteredLocations.map((entry, idx) => (
                      <button
                        key={entry.key}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          selectLocation(entry);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-[#1875FF]/5 hover:text-[#1875FF] transition-colors font-medium ${idx > 0 ? 'border-t border-gray-100' : ''}`}
                      >
                        {entry.label}
                      </button>
                    ))}
                  </div>
                )}
                {showLocationDropdown && locationSearch.trim() && filteredLocations.length === 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white border-2 border-gray-200 rounded-xl shadow-2xl shadow-[#1875FF]/20 px-4 py-3">
                    <p className="text-sm text-gray-400">No matching locations found</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-400 mt-1">Maximum {ICP_LIMITS.countries.max} countries reached</p>
            )}
          </div>

          {/* Industries */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Industries</label>
              <LimitBadge current={config.industry_ids.length} min={ICP_LIMITS.industries.min} max={ICP_LIMITS.industries.max} />
            </div>

            {config.industry_ids.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {config.industry_ids.map(id => (
                  <span key={id} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#05E0D1]/15 text-[#0e9e95] rounded-full text-sm font-medium">
                    {getIndustryName(id)}
                    <button onClick={() => toggleIndustry(id)} className="hover:bg-[#05E0D1]/25 rounded-full p-0.5 transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {config.industry_ids.length < ICP_LIMITS.industries.max && (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={industrySearch}
                    onChange={(e) => {
                      setIndustrySearch(e.target.value);
                      setShowIndustryDropdown(true);
                    }}
                    onFocus={() => setShowIndustryDropdown(true)}
                    onBlur={() => setTimeout(() => setShowIndustryDropdown(false), 200)}
                    placeholder="Search and select an industry..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all text-sm"
                  />
                </div>
                {showIndustryDropdown && filteredIndustries.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white border-2 border-gray-200 rounded-xl shadow-2xl shadow-[#1875FF]/20 max-h-48 overflow-auto">
                    {filteredIndustries.filter(i => !selectedIndustryIds.has(i.id)).map((industry, idx) => (
                      <button
                        key={industry.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          toggleIndustry(industry.id);
                          setIndustrySearch('');
                          setShowIndustryDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-[#1875FF]/5 hover:text-[#1875FF] transition-colors font-medium ${idx > 0 ? 'border-t border-gray-100' : ''}`}
                      >
                        {industry.industry_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {config.industry_ids.length >= ICP_LIMITS.industries.max && (
              <p className="text-xs text-gray-400 mt-1">Maximum {ICP_LIMITS.industries.max} industries reached</p>
            )}
            {config.industry_ids.length > 0 && config.industry_ids.length < ICP_LIMITS.industries.min && (
              <p className="text-xs text-red-500 mt-1">Select at least {ICP_LIMITS.industries.min} industries</p>
            )}
          </div>

          {/* Company Sizes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Company Sizes</label>
            <div className="flex flex-wrap gap-2">
              {COMPANY_SIZE_OPTIONS.map(size => (
                <button
                  key={size}
                  onClick={() => toggleCompanySize(size)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium border-2 transition-all ${
                    config.company_sizes.includes(size)
                      ? 'border-[#1875FF] bg-[#1875FF]/10 text-[#1875FF]'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {size} employees
                </button>
              ))}
            </div>
          </div>

          {/* Decision-Maker Titles */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Decision-Maker Titles</label>
              <LimitBadge current={config.title_ids.length} min={ICP_LIMITS.titles.min} max={ICP_LIMITS.titles.max} />
            </div>

            {config.title_ids.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {config.title_ids.map(id => (
                  <span key={id} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#1875FF]/10 text-[#1875FF] rounded-full text-sm font-medium">
                    {getTitleName(id)}
                    <button onClick={() => toggleTitle(id)} className="hover:bg-[#1875FF]/20 rounded-full p-0.5 transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {config.title_ids.length < ICP_LIMITS.titles.max && (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={titleSearch}
                    onChange={(e) => {
                      setTitleSearch(e.target.value);
                      setShowTitleDropdown(true);
                    }}
                    onFocus={() => setShowTitleDropdown(true)}
                    onBlur={() => setTimeout(() => setShowTitleDropdown(false), 200)}
                    placeholder="Search and select a title..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all text-sm"
                  />
                </div>
                {showTitleDropdown && titlesBySeniority.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white border-2 border-gray-200 rounded-xl shadow-2xl shadow-[#1875FF]/20 max-h-56 overflow-auto">
                    {titlesBySeniority.map(([level, titles], groupIdx) => (
                      <div key={level}>
                        <div className={`px-4 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50 sticky top-0 ${groupIdx > 0 ? 'border-t border-gray-100' : ''}`}>
                          {level}
                        </div>
                        {titles.map(title => (
                          <button
                            key={title.id}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              toggleTitle(title.id);
                              setTitleSearch('');
                            }}
                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-[#1875FF]/5 hover:text-[#1875FF] transition-colors font-medium border-t border-gray-50"
                          >
                            {title.title_name}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {config.title_ids.length >= ICP_LIMITS.titles.max && (
              <p className="text-xs text-gray-400 mt-1">Maximum {ICP_LIMITS.titles.max} titles reached</p>
            )}
            {config.title_ids.length > 0 && config.title_ids.length < ICP_LIMITS.titles.min && (
              <p className="text-xs text-red-500 mt-1">Select at least {ICP_LIMITS.titles.min} titles</p>
            )}
          </div>
        </div>
      </div>

      {/* Pain Points */}
      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-[box-shadow,border-color,transform] duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-[#1875FF]" />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900 font-display">Pain Points / Challenges</h3>
              <p className="text-sm text-gray-500">What problems does your product/service solve for customers?</p>
            </div>
          </div>
          <button onClick={addPainPoint} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#1875FF] bg-[#1875FF]/10 rounded-lg hover:bg-[#1875FF]/20 transition-colors">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
        <div className="space-y-3">
          {data.pain_points.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">No pain points added yet. Click "Add" to start.</p>
          )}
          {data.pain_points.map((point, index) => (
            <div key={point.id} className="flex items-start gap-3 group">
              <span className="mt-3 text-xs font-medium text-gray-400 w-5 text-right shrink-0">{index + 1}.</span>
              <BoldPrefixInput value={point.text} onChange={(e) => updatePainPoint(point.id, e.target.value)} placeholder="E.g.: Manual outreach is too time-consuming and inconsistent" className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all text-sm" />
              <button onClick={() => removePainPoint(point.id)} className="mt-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Benefits */}
      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-[box-shadow,border-color,transform] duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
              <TrendingUp className="w-5 h-5 text-[#1875FF]" />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900 font-display">Benefits to Clients</h3>
              <p className="text-sm text-gray-500">How are clients benefiting from your service/product?</p>
            </div>
          </div>
          <button onClick={addBenefit} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#1875FF] bg-[#1875FF]/10 rounded-lg hover:bg-[#1875FF]/20 transition-colors">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
        <div className="space-y-3">
          {data.benefits.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">No benefits added yet. Click "Add" to start.</p>
          )}
          {data.benefits.map((benefit, index) => (
            <div key={benefit.id} className="flex items-start gap-3 group">
              <span className="mt-3 text-xs font-medium text-gray-400 w-5 text-right shrink-0">{index + 1}.</span>
              <BoldPrefixInput value={benefit.text} onChange={(e) => updateBenefit(benefit.id, e.target.value)} placeholder="E.g.: Decreased costs by 40% through automation" className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all text-sm" />
              <button onClick={() => removeBenefit(benefit.id)} className="mt-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Solutions */}
      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-[box-shadow,border-color,transform] duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
              <Wrench className="w-5 h-5 text-[#1875FF]" />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900 font-display">Solutions</h3>
              <p className="text-sm text-gray-500">How your product/service solves each prospect challenge</p>
            </div>
          </div>
          <button onClick={addSolution} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#1875FF] bg-[#1875FF]/10 rounded-lg hover:bg-[#1875FF]/20 transition-colors">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
        <div className="space-y-3">
          {(data.solutions || []).length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">No solutions added yet. Click "Add" to start.</p>
          )}
          {(data.solutions || []).map((item, index) => (
            <div key={item.id} className="flex items-start gap-3 group">
              <span className="mt-3 text-xs font-medium text-gray-400 w-5 text-right shrink-0">{index + 1}.</span>
              <BoldPrefixInput value={item.text} onChange={(e) => updateSolution(item.id, e.target.value)} placeholder="E.g.: Automates cold outreach, eliminating 80% of manual follow-up work" className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all text-sm" />
              <button onClick={() => removeSolution(item.id)} className="mt-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Desires */}
      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-[box-shadow,border-color,transform] duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
              <Lightbulb className="w-5 h-5 text-[#1875FF]" />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900 font-display">Prospect Desires</h3>
              <p className="text-sm text-gray-500">What prospects want/desire in this area or department</p>
            </div>
          </div>
          <button onClick={addDesire} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#1875FF] bg-[#1875FF]/10 rounded-lg hover:bg-[#1875FF]/20 transition-colors">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
        <div className="space-y-3">
          {(data.desires || []).length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">No desires added yet. Click "Add" to start.</p>
          )}
          {(data.desires || []).map((item, index) => (
            <div key={item.id} className="flex items-start gap-3 group">
              <span className="mt-3 text-xs font-medium text-gray-400 w-5 text-right shrink-0">{index + 1}.</span>
              <BoldPrefixInput value={item.text} onChange={(e) => updateDesire(item.id, e.target.value)} placeholder="E.g.: Generate more qualified leads without increasing headcount" className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all text-sm" />
              <button onClick={() => removeDesire(item.id)} className="mt-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Prospect Questions */}
      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-[box-shadow,border-color,transform] duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
              <HelpCircle className="w-5 h-5 text-[#1875FF]" />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900 font-display">Prospect Questions</h3>
              <p className="text-sm text-gray-500">Common questions prospects have about this topic or problem area</p>
            </div>
          </div>
          <button onClick={addQuestion} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#1875FF] bg-[#1875FF]/10 rounded-lg hover:bg-[#1875FF]/20 transition-colors">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
        <div className="space-y-3">
          {(data.prospect_questions || []).length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">No questions added yet. Click "Add" to start.</p>
          )}
          {(data.prospect_questions || []).map((item, index) => (
            <div key={item.id} className="flex items-start gap-3 group">
              <span className="mt-3 text-xs font-medium text-gray-400 w-5 text-right shrink-0">{index + 1}.</span>
              <BoldPrefixInput value={item.text} onChange={(e) => updateQuestion(item.id, e.target.value)} placeholder="E.g.: How can we improve our sales pipeline conversion rate?" className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all text-sm" />
              <button onClick={() => removeQuestion(item.id)} className="mt-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
